import React, { useState, Fragment } from 'react';

import { IconButton, Collapse, Typography } from '@material-ui/core';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SettingsIcon from '@material-ui/icons/Settings';

import { PopupMenu } from 'react-frontend-utils'

import { ThemeColors } from '../Theme';

/**
 * Shows a list of Patrons for two types of tables: those where the Patrons signed up and those where a user manually added a Patron
 * The following props are expected:
 * 
 * patronList (array): An array of Patrons
 * selectPatronForCheckIn (function): Callback to select the Patron for Check-In, passed the Patron
 * subtractPatronCheckIn (function): Callback to subtract count from a Patron Check-In, passed the Patron
 * viewPatronQuestions (function): Callback to view Patron Questions, passed the Patron
 */
export function PatronCheckInTable(props) {

    const styles = {
        table: {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: 10,
            marginTop: 10
        },
        tableHeader: {
            borderBottom: '2px solid ' + ThemeColors.appBarBackground,
            textAlign: 'left',
            paddingBottom: 5,
            paddingRight: 10,
            marginBottom: 8,
            fontSize: 13,
            color: ThemeColors.darkGray,
            fontWeight: 'normal',
            textTransform: 'uppercase'
        },
        tableDataWithBorder: {
            textAlign: 'left',
            fontSize: 13,
            paddingRight: 10,
            borderBottom: '1px solid lightGray'
        },
        tableData: {
            textAlign: 'left',
            fontSize: 14,
            marginRight: 10,
            verticalAlign: 'center'
        },
        checkedIn: {
            margin: 'auto',
            alignContent: 'center',
            width: 170,
            textTransform: 'uppercase',
            padding: 2, 
            borderRadius: 2, 
            color: 'white', 
            fontSize: 16,
            fontWeight: 'bold',
            textAlign: 'center'
        },
        details: {
            marginLeft: 30,
            marginBottom: 5,
            padding: 5,
            textAlign: 'left',
            borderRadius: 3,
            backgroundColor: ThemeColors.veryLightGray,
        },
    };

    const isWide = window.innerWidth > 1200;

    const columns = isWide ? 7 : 5;

    const [revealSet, setRevealSet] = useState(new Set());


    //If the Patron is in the reveal list, remove it, if it is not, add it (toggle the view)
    const revealDetail = (patron) => {
        
        if (revealSet.has(patron.id))
            revealSet.delete(patron.id);
        else
            revealSet.add(patron.id);
        
        setRevealSet(new Set(revealSet));
    }

    const getActionItems = (patron) => {
        
        const actionItems = [];

        if (patron.canCheckInMore())
            actionItems.push({label: "Select Patron for Check-In", selectCallback: () => props.selectPatronForCheckIn(patron), icon: null});

        if (patron.canRemoveCheckIn())
            actionItems.push({label: "Undo Last Check-In", selectCallback: () => props.subtractPatronCheckIn(patron), icon: null});

        if (patron.hasPatronQuestions())
            actionItems.push({label: "View Answers to Questions", selectCallback: () => props.viewPatronQuestions(patron), icon: null});


        return actionItems;
    }
    


    const renderRow = (patron, key) => {
            
        const isRevealed = revealSet.has(patron.id);
        const leftIcon = isRevealed ? <ArrowDropDownIcon/> : <ArrowRightIcon/>;
        
        const checkedInCount = patron.checkedInCount ? patron.checkedInCount : 0;

        let checkedInColor = 'gray';
        if (checkedInCount === patron.patronCount)
            checkedInColor = 'green';
        else if (checkedInCount > 0)
            checkedInColor = 'blue';

        return (
            <Fragment key={key}>
                <tr style={styles.tableStyle}>

                    <td style={styles.tableData}>
                        <IconButton edge="start" onClick={() => revealDetail(patron)}>
                            {leftIcon}
                        </IconButton>
                    </td>

                    <td style={styles.tableData}>{patron.getPrimaryPatron()}</td>
                    <td style={styles.tableData}>
                        <div style={{...styles.checkedIn, textAlign: 'center', backgroundColor: checkedInColor}}>
                            {checkedInCount + " / " + patron.patronCount}
                        </div>
                    </td>
                    {isWide ?
                        <Fragment>
                            <td style={{...styles.tableData, paddingLeft: 20}}>{patron.patronEmail}</td>
                            <td style={styles.tableData}>{patron.patronPhone + (patron.enableSms ? " (SMS)" : "")}</td>
                        </Fragment>
                        : null
                    }
                    <td style={{...styles.tableData, paddingRight: 30}}>{patron.paymentBadge()}</td>

                    <td style={{...styles.tableData, paddingRight: 0}}>
                        <PopupMenu menuIcon={(<SettingsIcon style={{fontSize: '20', color: ThemeColors.settingsOlive}}/>)}  
                                    menuItems={getActionItems(patron)} 
                                    menuTooltipText={"Actions"}/>
                    </td>
                </tr>
                <tr style={styles.tableStyle}>
                    <td colSpan={columns} align='center' style={styles.tableDataWithBorder}>
                        <Collapse in={isRevealed}>
                            <Typography variant='body2' style={{marginLeft: 35, fontWeight: 'bold'}}>Details</Typography>
                            <div style={styles.details} dangerouslySetInnerHTML={{ __html: patron.details() }} />
                        </Collapse>
                    </td>
                </tr>
            </Fragment>
        );
    }

    return (
        <table style={styles.table}>
            <thead>
                <tr style={styles.tableStyle}>
                    <th style={{...styles.tableHeader, paddingRight: 0, width: '30px'}}/>
                    <th style={{...styles.tableHeader, width: 180}}>Primary Patron</th>
                    <th style={{...styles.tableHeader, width: 60, textAlign: 'center'}}>Checked-In</th>
                    {isWide ?  
                        <Fragment>
                            <th style={{...styles.tableHeader, width: 180, paddingLeft: 20}}>Email</th>
                            <th style={{...styles.tableHeader, width: 160}}>Phone</th>
                        </Fragment>
                        : null
                    }
                    <th style={{...styles.tableHeader, textAlign: 'center', width: 160, paddingRight: 30}}>Payments</th>
                    <th style={{...styles.tableHeader, paddingRight: 0, width: '30px'}}/>
                </tr>
            </thead>
            <tbody>
                {props.patronList.map((patron, index) => renderRow(patron, index))}
            </tbody>
        </table>
    );


}